import { faXmark as CancelIcon } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import type { AxiosError } from 'axios';
import { useAtom, useAtomValue } from 'jotai';
import { sortBy } from 'lodash';
import { useMemo } from 'react';

import DualListSelect from '@/components/DualListSelect';
import {
  qbFiltersAtom,
  qbTimeFilterAtom
} from '@/components/QueryBuilder/QueryBuilder.hooks';
import {
  EXPORT_COLUMNS_TICKETS,
  EXPORT_COLUMNS_TICKETS_INTERNAL
} from '@/constants/exportcolumns';
import useHandleError from '@/hooks/useHandleError';
import usePermissionsCheck from '@/hooks/usePermissionsCheck.tsx';
import { selectedOrganizationAtom } from '@/pages/Organizations/Organizations.store';
import {
  exportedTicketsColumnSelectionAtom,
  mapFiltersToRequestParams,
  useFetchTicketExport
} from '@/pages/Tickets/Tickets.store';

import useStyles from './TicketExportDialog.styles';

const TicketExportDialog = ({
  handleClose,
  open
}: {
  handleClose: () => void;
  open: boolean;
}) => {
  const [selectedColumns, setSelectedColumns] = useAtom(
    exportedTicketsColumnSelectionAtom
  );
  const userIsInternal = usePermissionsCheck('internal');
  const ticketTimeFilter = useAtomValue(qbTimeFilterAtom);
  const selectedOrganization = useAtomValue(selectedOrganizationAtom);
  const ticketFilters = useAtomValue(qbFiltersAtom);
  const { mutateAsync: fetchTicketExport, isPending } = useFetchTicketExport();
  const handleError = useHandleError();
  const { classes } = useStyles();

  const handelCancel = () => {
    handleClose();
  };

  const triggerFileDownload = (blob: Blob, format: 'json' | 'csv') => {
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    const dateLabel = new Date().toISOString().split('T')[0];

    link.href = objectUrl;
    link.download = `tickets-export-${dateLabel}.${format}`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(objectUrl);
  };

  const handleExport = async (format: 'json' | 'csv') => {
    try {
      const mappedFilters = mapFiltersToRequestParams(
        ticketFilters || [],
        ticketTimeFilter,
        'published_at'
      );
      if (selectedOrganization.id > 0) {
        mappedFilters.organization__label = selectedOrganization?.label || '';
      }

      const response = await fetchTicketExport({
        format,
        columns: selectedColumns,
        ticketFilters: mappedFilters
      });

      const mimeType =
        format === 'csv' ? 'text/csv;charset=utf-8' : 'application/json';
      const blob = new Blob([response.data], { type: mimeType });
      triggerFileDownload(blob, format);
      handleClose();
    } catch (error) {
      handleError(error as AxiosError, 'Failure to export tickets.');
    }
  };

  const options = useMemo(() => {
    return sortBy(
      Object.keys(
        userIsInternal
          ? EXPORT_COLUMNS_TICKETS_INTERNAL
          : EXPORT_COLUMNS_TICKETS
      ).map((col) => ({
        value: col,
        label: col.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
      })),
      'label'
    );
  }, [userIsInternal]);

  return (
    <Dialog fullWidth open={open} maxWidth="md">
      <DialogTitle>Ticket Export Column Selection</DialogTitle>
      <IconButton
        className={classes.ticketExportDialogIconButtonCancel}
        onClick={handelCancel}
      >
        <FontAwesomeIcon size="xs" icon={CancelIcon} />
      </IconButton>
      <DialogContent className={classes.ticketExportDialogDialogContent}>
        <DualListSelect
          labelAvailable="Available Columns"
          labelSelected="Selected Columns"
          options={options}
          selected={selectedColumns}
          onChange={setSelectedColumns}
        />
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={handelCancel}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={selectedColumns.length === 0 || isPending}
          type="submit"
          onClick={() => handleExport('csv')}
        >
          Export CSV
        </Button>
        <Button
          variant="contained"
          disabled={selectedColumns.length === 0 || isPending}
          type="submit"
          onClick={() => handleExport('json')}
        >
          Export JSON
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TicketExportDialog;
